/**
 * ScrollProgressCalculator - Advanced scroll-based animation progress calculator
 */

export class ScrollProgressCalculator {
    constructor(element, config = {}) {
        this.element = element;
        this.config = {
            animationStart: 0,      // % when animation starts
            animationEnd: 100,      // % when animation ends
            scrollDirection: 'vertical',
            startOffset: 0,         // px offset from start
            endOffset: 0,           // px offset from end
            refreshPriority: 0,     // 0 = normal, 1 = high, 2 = very high
            ...config
        };
        
        this.callbacks = {
            onProgress: null,
            onStart: null,
            onComplete: null,
            onEnter: null,
            onLeave: null
        };
        
        this.state = {
            progress: 0,            // 0-1 animation progress
            visibility: 0,          // 0-100 element visibility %
            isInView: false,
            hasStarted: false,
            hasCompleted: false,
            lastScrollY: 0,
            lastScrollX: 0
        };
        
        this.rafId = null;
        this.isActive = false;
        
        this.init();
    }
    
    init() {
        this.bindEvents();
        this.update(); // Initial calculation
    }
    
    bindEvents() {
        // Use different refresh rates based on priority
        const eventOptions = { passive: true };
        
        if (this.config.refreshPriority === 0) {
            // Normal priority - throttled
            let ticking = false;
            this.scrollHandler = () => {
                if (!ticking) {
                    requestAnimationFrame(() => {
                        this.update();
                        ticking = false;
                    });
                    ticking = true;
                }
            };
        } else if (this.config.refreshPriority === 1) {
            // High priority - every frame
            this.scrollHandler = () => {
                if (this.rafId) cancelAnimationFrame(this.rafId);
                this.rafId = requestAnimationFrame(() => this.update());
            };
        } else {
            // Very high priority - immediate
            this.scrollHandler = () => this.update();
        }
        
        if (this.config.scrollDirection === 'vertical') {
            window.addEventListener('scroll', this.scrollHandler, eventOptions);
        } else {
            // Horizontal scroll detection
            let scrollContainer = this.findScrollContainer();
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', this.scrollHandler, eventOptions);
            } else {
                window.addEventListener('scroll', this.scrollHandler, eventOptions);
            }
        }
        
        window.addEventListener('resize', this.scrollHandler, eventOptions);
    }
    
    findScrollContainer() {
        // Find horizontal scroll container
        let parent = this.element.parentElement;
        
        while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent);
            const overflowX = style.overflowX;
            
            if (overflowX === 'scroll' || overflowX === 'auto') {
                if (parent.scrollWidth > parent.clientWidth) {
                    return parent;
                }
            }
            
            parent = parent.parentElement;
        }
        
        return null;
    }
    
    update() {
        const rect = this.element.getBoundingClientRect();
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };
        
        let progress, visibility;
        
        if (this.config.scrollDirection === 'vertical') {
            ({ progress, visibility } = this.calculateVerticalProgress(rect, viewport));
        } else {
            ({ progress, visibility } = this.calculateHorizontalProgress(rect, viewport));
        }
        
        // Update state
        const prevState = { ...this.state };
        this.state.progress = progress;
        this.state.visibility = visibility;
        this.state.isInView = visibility > 0;
        
        // Check for state changes
        this.checkStateTransitions(prevState);
        
        // Call progress callback
        if (this.callbacks.onProgress) {
            this.callbacks.onProgress(progress, visibility, this.state);
        }
    }
    
    calculateVerticalProgress(rect, viewport) {
        const elementTop = rect.top;
        const elementBottom = rect.bottom;
        const elementHeight = rect.height;
        
        // Calculate visibility percentage (0-100)
        let visibility = 0;
        
        if (elementBottom > 0 && elementTop < viewport.height) {
            const visibleTop = Math.max(0, -elementTop);
            const visibleBottom = Math.min(elementHeight, viewport.height - elementTop);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            visibility = (visibleHeight / elementHeight) * 100;
        }
        
        // Calculate progress based on animation area
        let progress = 0;
        
        if (visibility > 0) {
            // Element position in viewport (0 = top of viewport, 1 = bottom of viewport)
            const elementCenterY = elementTop + elementHeight / 2;
            const normalizedPosition = elementCenterY / viewport.height;
            
            // Map to animation range
            const animationRange = this.config.animationEnd - this.config.animationStart;
            const progressInRange = Math.max(0, Math.min(100, 
                ((100 - normalizedPosition * 100) - this.config.animationStart) / animationRange * 100
            ));
            
            progress = Math.max(0, Math.min(1, progressInRange / 100));
            
            // Apply offsets
            if (this.config.startOffset !== 0 || this.config.endOffset !== 0) {
                const adjustedProgress = this.applyOffsets(progress, elementTop, viewport.height);
                progress = adjustedProgress;
            }
        }
        
        return { progress, visibility };
    }
    
    calculateHorizontalProgress(rect, viewport) {
        const elementLeft = rect.left;
        const elementRight = rect.right;
        const elementWidth = rect.width;
        
        // Calculate visibility percentage (0-100)
        let visibility = 0;
        
        if (elementRight > 0 && elementLeft < viewport.width) {
            const visibleLeft = Math.max(0, -elementLeft);
            const visibleRight = Math.min(elementWidth, viewport.width - elementLeft);
            const visibleWidth = Math.max(0, visibleRight - visibleLeft);
            visibility = (visibleWidth / elementWidth) * 100;
        }
        
        // Calculate progress based on animation area
        let progress = 0;
        
        if (visibility > 0) {
            const elementCenterX = elementLeft + elementWidth / 2;
            const normalizedPosition = elementCenterX / viewport.width;
            
            const animationRange = this.config.animationEnd - this.config.animationStart;
            const progressInRange = Math.max(0, Math.min(100, 
                ((100 - normalizedPosition * 100) - this.config.animationStart) / animationRange * 100
            ));
            
            progress = Math.max(0, Math.min(1, progressInRange / 100));
        }
        
        return { progress, visibility };
    }
    
    applyOffsets(progress, elementPosition, viewportSize) {
        const startOffsetNormalized = this.config.startOffset / viewportSize;
        const endOffsetNormalized = this.config.endOffset / viewportSize;
        
        // Adjust progress based on offsets
        const adjustedProgress = (progress - startOffsetNormalized) / (1 - startOffsetNormalized - endOffsetNormalized);
        
        return Math.max(0, Math.min(1, adjustedProgress));
    }
    
    checkStateTransitions(prevState) {
        // Check if animation should start
        if (!this.state.hasStarted && this.state.progress > 0) {
            this.state.hasStarted = true;
            if (this.callbacks.onStart) {
                this.callbacks.onStart(this.state);
            }
        }
        
        // Check if animation is complete
        if (!this.state.hasCompleted && this.state.progress >= 1) {
            this.state.hasCompleted = true;
            if (this.callbacks.onComplete) {
                this.callbacks.onComplete(this.state);
            }
        }
        
        // Check enter/leave viewport
        if (!prevState.isInView && this.state.isInView) {
            if (this.callbacks.onEnter) {
                this.callbacks.onEnter(this.state);
            }
        } else if (prevState.isInView && !this.state.isInView) {
            if (this.callbacks.onLeave) {
                this.callbacks.onLeave(this.state);
            }
        }
        
        // Reset states if element exits and re-enters
        if (!this.state.isInView) {
            this.state.hasStarted = false;
            this.state.hasCompleted = false;
        }
    }
    
    // Public API methods
    
    /**
     * Set progress callback
     * @param {Function} callback - (progress, visibility, state) => void
     */
    onProgress(callback) {
        this.callbacks.onProgress = callback;
        return this;
    }
    
    /**
     * Set start callback
     * @param {Function} callback - (state) => void
     */
    onStart(callback) {
        this.callbacks.onStart = callback;
        return this;
    }
    
    /**
     * Set complete callback
     * @param {Function} callback - (state) => void
     */
    onComplete(callback) {
        this.callbacks.onComplete = callback;
        return this;
    }
    
    /**
     * Set enter viewport callback
     * @param {Function} callback - (state) => void
     */
    onEnter(callback) {
        this.callbacks.onEnter = callback;
        return this;
    }
    
    /**
     * Set leave viewport callback
     * @param {Function} callback - (state) => void
     */
    onLeave(callback) {
        this.callbacks.onLeave = callback;
        return this;
    }
    
    /**
     * Update configuration
     * @param {Object} newConfig - New configuration
     */
    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
        this.update(); // Recalculate with new config
        return this;
    }
    
    /**
     * Get current progress (0-1)
     * @returns {number}
     */
    getProgress() {
        return this.state.progress;
    }
    
    /**
     * Get current visibility (0-100)
     * @returns {number}
     */
    getVisibility() {
        return this.state.visibility;
    }
    
    /**
     * Get current state
     * @returns {Object}
     */
    getState() {
        return { ...this.state };
    }
    
    /**
     * Force update calculation
     */
    refresh() {
        this.update();
        return this;
    }
    
    /**
     * Destroy the calculator
     */
    destroy() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }
        
        window.removeEventListener('scroll', this.scrollHandler);
        window.removeEventListener('resize', this.scrollHandler);
        
        // Find and remove horizontal scroll listener if exists
        const scrollContainer = this.findScrollContainer();
        if (scrollContainer) {
            scrollContainer.removeEventListener('scroll', this.scrollHandler);
        }
        
        this.callbacks = {};
        this.isActive = false;
    }
    
    /**
     * Pause calculations
     */
    pause() {
        this.isActive = false;
        return this;
    }
    
    /**
     * Resume calculations
     */
    resume() {
        this.isActive = true;
        this.update();
        return this;
    }
}

// Factory function for easy creation
export function createScrollProgressCalculator(element, config) {
    return new ScrollProgressCalculator(element, config);
}

// Utility functions

/**
 * Create multiple calculators for elements
 * @param {NodeList|Array} elements - Elements to create calculators for
 * @param {Object} config - Configuration for all calculators
 * @returns {Array} Array of calculators
 */
export function createMultipleCalculators(elements, config) {
    return Array.from(elements).map(element => 
        new ScrollProgressCalculator(element, config)
    );
}

/**
 * Batch update multiple calculators
 * @param {Array} calculators - Array of calculators
 */
export function batchUpdateCalculators(calculators) {
    requestAnimationFrame(() => {
        calculators.forEach(calc => calc.update());
    });
}

/**
 * Create synchronized calculators that update together
 * @param {NodeList|Array} elements - Elements to sync
 * @param {Object} config - Configuration
 * @returns {Object} Sync controller
 */
export function createSyncedCalculators(elements, config) {
    const calculators = createMultipleCalculators(elements, config);
    
    let rafId = null;
    const updateAll = () => {
        calculators.forEach(calc => calc.update());
    };
    
    const throttledUpdate = () => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(updateAll);
    };
    
    // Override individual scroll handlers with synced one
    window.addEventListener('scroll', throttledUpdate, { passive: true });
    window.addEventListener('resize', throttledUpdate, { passive: true });
    
    return {
        calculators,
        update: updateAll,
        destroy: () => {
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener('scroll', throttledUpdate);
            window.removeEventListener('resize', throttledUpdate);
            calculators.forEach(calc => calc.destroy());
        }
    };
}

export default ScrollProgressCalculator;